import type {
  RuntimeTreeInstance,
  RuntimeWorldTilePayloadV2,
} from "./schema/runtimeTile.ts";
import type { TerrainTileData } from "./schema/tile.ts";
import { sampleTerrainTileData } from "./terrain.ts";

/**
 * Returns the vegetation evidence embedded by the offline world builder.
 *
 * Municipal inventory trees used to be reconstructed from an 853 KiB source
 * snapshot in every browser session. The wide-area exporter now owns that
 * transformation, so loading the survey again at runtime duplicated data and
 * delayed the first playable tile. Runtime rendering deliberately consumes
 * only the independently streamed tile payload.
 */
export function vegetationInstancesForTile(
  tile: Readonly<RuntimeWorldTilePayloadV2>,
  groundingTerrain: Readonly<TerrainTileData> = tile.terrain,
): readonly RuntimeTreeInstance[] {
  return (tile.vegetation ?? [])
    .map((tree) => groundTreeToTerrain(tree, tile.terrain, groundingTerrain))
    .sort(compareById);
}

function groundTreeToTerrain(
  tree: Readonly<RuntimeTreeInstance>,
  sourceTerrain: Readonly<TerrainTileData>,
  groundingTerrain: Readonly<TerrainTileData>,
): Readonly<RuntimeTreeInstance> {
  if (sourceTerrain === groundingTerrain) {
    return tree;
  }
  const sourceElevationM = sampleTerrainTileData(
    sourceTerrain,
    { x: 0, z: 0 },
    tree.position,
  );
  const groundingElevationM = sampleTerrainTileData(
    groundingTerrain,
    { x: 0, z: 0 },
    tree.position,
  );
  if (sourceElevationM === null || groundingElevationM === null) {
    return tree;
  }
  const elevationDeltaM = groundingElevationM - sourceElevationM;
  return Math.abs(elevationDeltaM) <= 1e-6
    ? tree
    : { ...tree, baseElevationM: tree.baseElevationM + elevationDeltaM };
}

function compareById(
  left: Readonly<{ id: string }>,
  right: Readonly<{ id: string }>,
): number {
  return left.id < right.id ? -1 : left.id > right.id ? 1 : 0;
}
